import base64ToUint8Array from '@contentlab/utils/base64';
import {parseSseBlocks, coercePythonStyleJson} from '@contentlab/utils/sse';
import {createWavBlobFromPcmBytes, hasWavHeader} from '@contentlab/utils/wav';

type MapChunkFn = (
  json: unknown,
  eventName: string | null
) => {
  base64?: string;
  mime?: string;
  done?: boolean;
};

export default async function streamSseToBlob({
  url,
  headers,
  body,
  mapChunk,
  signal,
  onChunk,
}: {
  url: string;
  headers: Record<string, string>;
  body: unknown;
  mapChunk: MapChunkFn;
  signal?: AbortSignal;
  onChunk?: (bytesReceived: number) => void;
}): Promise<{audioUrl: string; mimeType: string}> {
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok || !response.body) {
    const textResp = await response.text().catch(() => '');
    throw new Error(
      `Stream request failed: ${response.status} ${response.statusText} ${textResp}`
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let bytesReceived = 0;
  const chunks: Uint8Array[] = [];
  let mimeType = 'audio/wav';
  let ended = false;

  try {
    const readNext = async (): Promise<void> => {
      const {done, value} = await reader.read();
      if (done || ended) return;
      buffer += decoder.decode(value, {stream: true});
      const {blocks, remainder} = parseSseBlocks(buffer);
      buffer = remainder;
      for (let i = 0; i < blocks.length; i += 1) {
        const block = blocks[i];
        const lines = block.split(/\n/).map((l) => l.trim());
        let eventName: string | null = null;
        const dataLines: string[] = [];
        for (let j = 0; j < lines.length; j += 1) {
          const line = lines[j];
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:'))
            dataLines.push(line.slice(5).trim());
        }
        const dataStr = dataLines.join('\n');
        if (dataStr) {
          let jsonData: unknown = null;
          try {
            jsonData = JSON.parse(dataStr);
          } catch {
            jsonData = JSON.parse(coercePythonStyleJson(dataStr));
          }
          const mapped = mapChunk(jsonData, eventName);
          if (mapped.done) {
            ended = true;
            break;
          }
          if (mapped.mime) mimeType = mapped.mime;
          if (mapped.base64) {
            const bytes = base64ToUint8Array(mapped.base64);
            chunks.push(bytes);
            bytesReceived += bytes.length;
            onChunk?.(bytesReceived);
          }
        }
      }
      if (!ended) await readNext();
    };
    await readNext();
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* empty */
    }
  }

  if (chunks.length === 0) throw new Error('Stream produced no audio data');

  let totalLen = 0;
  for (let i = 0; i < chunks.length; i += 1) totalLen += chunks[i].length;
  const flat = new Uint8Array(totalLen);
  let offset = 0;
  for (let i = 0; i < chunks.length; i += 1) {
    flat.set(chunks[i], offset);
    offset += chunks[i].length;
  }

  const blob = hasWavHeader(flat)
    ? new Blob([flat], {type: mimeType || 'audio/wav'})
    : createWavBlobFromPcmBytes(flat);
  const audioUrl = URL.createObjectURL(blob);
  return {audioUrl, mimeType};
}
